/**
 * SEO Content Agent
 * Generates SEO-optimized content for pages
 */

import { seasonalProduceAgent } from './index';

interface SEOPage {
  slug: string;
  title: string;
  metaDescription: string;
  h1: string;
  content: string;
  keywords: string[];
  schema: any;
  relatedPages: string[];
}

class SEOContentAgent {
  /**
   * Generate location-based SEO page
   */
  generateLocationPage(city: string, type: string = 'farms'): SEOPage {
    const slug = `${type}-${city.toLowerCase().replace(/\s+/g, '-')}`;

    return {
      slug,
      title: `Best ${type} in ${city}, Florida | SWFL Local Farms`,
      metaDescription: `Discover the best ${type} in ${city}, FL. Fresh, local, and organic produce from Southwest Florida farms and markets. Find farm stands, farmers markets, and CSA programs near you.`,
      h1: `${type.charAt(0).toUpperCase() + type.slice(1)} in ${city}, Florida`,
      content: this.generateLocationContent(city, type),
      keywords: [
        `${city} ${type}`,
        `${type} in ${city} Florida`,
        `Southwest Florida ${type}`,
        `${city} farmers market`,
        `organic ${type} ${city}`,
        `local farms ${city}`,
        `${city} fresh produce`,
      ],
      schema: this.generateLocationSchema(city, type),
      relatedPages: [
        `/browse`,
        `/map`,
        `/seasonal`,
      ],
    };
  }

  /**
   * Generate product-based SEO page
   */
  generateProductPage(product: string, location?: string): SEOPage {
    const slug = location
      ? `${product.toLowerCase()}-${location.toLowerCase().replace(/\s+/g, '-')}`
      : product.toLowerCase().replace(/\s+/g, '-');

    const locationText = location ? ` in ${location}` : ' in Southwest Florida';

    return {
      slug,
      title: `Fresh ${product}${locationText} | Local Farms & Markets`,
      metaDescription: `Find fresh, locally grown ${product}${locationText}. Discover where to buy ${product} from local farms, farmers markets, and farm stands. Seasonal availability and recipes included.`,
      h1: `Fresh ${product}${locationText}`,
      content: this.generateProductContent(product, location),
      keywords: [
        `${product}${locationText}`,
        `fresh ${product}`,
        `organic ${product}`,
        `${product} farm`,
        `where to buy ${product}`,
        `${product} season`,
      ],
      schema: this.generateProductSchema(product, location),
      relatedPages: [
        `/browse`,
        `/seasonal`,
      ],
    };
  }

  /**
   * Generate seasonal SEO page
   */
  generateSeasonalPage(month: number): SEOPage {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = monthNames[month - 1];
    const slug = `seasonal-${monthName.toLowerCase()}`;

    const seasonalData = seasonalProduceAgent.getSeasonalRecommendations(month);

    return {
      slug,
      title: `What's in Season in ${monthName} | Southwest Florida Produce Guide`,
      metaDescription: `Discover what's fresh and in season in ${monthName} in Southwest Florida. Find local farms and markets with seasonal produce. Plan your farm visits and recipes.`,
      h1: `${monthName} Seasonal Produce Guide`,
      content: this.generateSeasonalContent(monthName, seasonalData),
      keywords: [
        `${monthName} produce Florida`,
        `seasonal ${monthName}`,
        `what's in season ${monthName}`,
        `Florida ${monthName} harvest`,
        `${monthName} farmers market`,
      ],
      schema: this.generateSeasonalSchema(monthName, seasonalData),
      relatedPages: [
        `/seasonal`,
        `/browse`,
      ],
    };
  }

  /**
   * Generate comparison SEO page
   */
  generateComparisonPage(topic: string): SEOPage {
    const slug = `${topic.toLowerCase().replace(/\s+/g, '-')}`;

    return {
      slug,
      title: `${topic} | SWFL Local Farms Guide`,
      metaDescription: `Compare and discover ${topic.toLowerCase()} in Southwest Florida. Expert guide to local farms, markets, and produce.`,
      h1: topic,
      content: this.generateComparisonContent(topic),
      keywords: [
        topic,
        `${topic} guide`,
        `best ${topic}`,
        `${topic} Southwest Florida`,
      ],
      schema: this.generateArticleSchema(topic),
      relatedPages: [
        `/browse`,
        `/about`,
      ],
    };
  }

  /**
   * Generate guide SEO page
   */
  generateGuidePage(topic: string, subtopics: string[]): SEOPage {
    const slug = `guide-${topic.toLowerCase().replace(/\s+/g, '-')}`;

    return {
      slug,
      title: `Complete Guide to ${topic} | SWFL Local Farms`,
      metaDescription: `Your complete guide to ${topic.toLowerCase()} in Southwest Florida. Tips, recommendations, and everything you need to know about local farms and markets.`,
      h1: `Complete Guide to ${topic}`,
      content: this.generateGuideContent(topic, subtopics),
      keywords: [
        `${topic} guide`,
        `how to ${topic}`,
        `${topic} tips`,
        `${topic} Southwest Florida`,
      ],
      schema: this.generateHowToSchema(topic, subtopics),
      relatedPages: [
        `/browse`,
        `/seasonal`,
      ],
    };
  }

  /**
   * Generate location content
   */
  private generateLocationContent(city: string, type: string): string {
    return `
## Discover ${type} in ${city}, Florida

Looking for fresh, locally grown produce in ${city}? You've come to the right place! Southwest Florida is home to numerous ${type} offering fresh, seasonal produce year-round.

### Why Choose Local ${type} in ${city}?

- **Freshness**: Produce is harvested at peak ripeness
- **Quality**: Support local agriculture and small businesses
- **Sustainability**: Reduce food miles and environmental impact
- **Community**: Connect with local farmers and food producers

### What to Expect

${type.charAt(0).toUpperCase() + type.slice(1)} in ${city} offer a variety of fresh produce, including vegetables, fruits, herbs, and specialty items. Many also provide:

- U-Pick experiences
- Farm tours and educational programs
- CSA (Community Supported Agriculture) programs
- Online ordering and delivery
- Seasonal events and workshops

### Seasonal Availability

Southwest Florida's subtropical climate allows for year-round growing, with peak seasons for different crops throughout the year. Visit our [Seasonal Guide](/seasonal) to learn what's fresh each month.

### Getting Started

1. Browse our directory of ${type} in ${city}
2. Check operating hours and availability
3. Plan your visit or order online
4. Enjoy fresh, local produce!

Ready to discover the best ${type} in ${city}? Browse our complete directory below.
    `.trim();
  }

  /**
   * Generate product content
   */
  private generateProductContent(product: string, location?: string): string {
    const locationText = location || 'Southwest Florida';

    return `
## Fresh ${product} from Local Farms

Discover where to find the freshest ${product} ${location ? `in ${location}` : 'in Southwest Florida'}. Our local farms and markets offer high-quality, locally grown ${product} throughout the season.

### About ${product}

${product} is a popular crop in Southwest Florida, grown by local farmers who take pride in their produce. When you buy local ${product}, you're getting:

- Freshly harvested, peak-ripeness produce
- Support for local agriculture
- Better flavor and nutrition
- Reduced environmental impact

### Where to Buy

Find ${product} at:
- Local farm stands
- Farmers markets
- CSA programs
- U-Pick farms

### Seasonal Availability

Check our [Seasonal Guide](/seasonal) to learn when ${product} is at its peak in ${locationText}.

### Recipes and Tips

- Storage tips to keep ${product} fresh longer
- Recipe ideas using ${product}
- Preparation and cooking methods

Browse farms and markets offering ${product} below.
    `.trim();
  }

  /**
   * Generate seasonal content
   */
  private generateSeasonalContent(month: string, seasonalData: any): string {
    const producList = seasonalData.peakProduce.map((p: any) => p.name).join(', ');

    return `
## What's Fresh in ${month}

${month} is a great time for local produce in Southwest Florida! Here's what's at its peak this month.

### Peak Produce This Month

${producList}

### Why Eat Seasonally?

- Best flavor and nutrition
- Lower prices
- Support local farmers
- Environmentally sustainable

### ${month} Highlights

${seasonalData.tips.map((tip: string) => `- ${tip}`).join('\n')}

### Where to Find Seasonal Produce

Visit local farmers markets, farm stands, and CSA programs to get the freshest ${month} produce. Check our [Browse page](/browse) to find locations near you.

### Recipe Ideas

Looking for ways to use this month's harvest? Check out our recipe suggestions featuring seasonal ingredients.

Browse farms and markets offering fresh ${month} produce below.
    `.trim();
  }

  /**
   * Generate comparison content
   */
  private generateComparisonContent(topic: string): string {
    return `
## ${topic}

Explore and compare ${topic.toLowerCase()} in Southwest Florida. This guide helps you make informed decisions about where to shop for fresh, local produce.

### What We Cover

- Detailed comparisons
- Key features and benefits
- Location and accessibility
- Product offerings
- Customer reviews and ratings

### Making the Right Choice

When choosing ${topic.toLowerCase()}, consider:

1. **Location**: How far are you willing to travel?
2. **Products**: What types of produce are you looking for?
3. **Certifications**: Do you prefer organic or certified options?
4. **Services**: Do you want delivery, U-Pick, or CSA programs?

Browse and compare options below.
    `.trim();
  }

  /**
   * Generate guide content
   */
  private generateGuideContent(topic: string, subtopics: string[]): string {
    return `
## Your Complete Guide to ${topic}

Everything you need to know about ${topic.toLowerCase()} in Southwest Florida.

### What You'll Learn

${subtopics.map(sub => `- ${sub}`).join('\n')}

### Getting Started

This comprehensive guide covers all aspects of ${topic.toLowerCase()}, from finding the best locations to making the most of your experience.

### Why This Guide?

- Expert recommendations
- Local insights
- Practical tips
- Updated information

Explore the sections below to learn more.
    `.trim();
  }

  /**
   * Generate location schema
   */
  private generateLocationSchema(city: string, type: string): any {
    return {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${type} in ${city}, Florida`,
      description: `Directory of ${type} in ${city}, Southwest Florida`,
      url: `https://swfllocalfarms.com/${type}-${city.toLowerCase().replace(/\s+/g, '-')}`,
    };
  }

  /**
   * Generate product schema
   */
  private generateProductSchema(product: string, location?: string): any {
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product,
      description: `Fresh ${product} from local farms`,
    };
  }

  /**
   * Generate seasonal schema
   */
  private generateSeasonalSchema(month: string, data: any): any {
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `What's in Season in ${month}`,
      description: `Seasonal produce guide for ${month} in Southwest Florida`,
    };
  }

  /**
   * Generate article schema
   */
  private generateArticleSchema(topic: string): any {
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: topic,
    };
  }

  /**
   * Generate how-to schema
   */
  private generateHowToSchema(topic: string, steps: string[]): any {
    return {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: `Complete Guide to ${topic}`,
      step: steps.map((step, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: step,
      })),
    };
  }
}

export default new SEOContentAgent();
